import {Trans, t} from '@lingui/macro'
import type {FC} from 'react'
import {useState} from 'react'
import {Link as RouterLink} from 'react-router-dom'
import styled from 'styled-components'

import {
    Button,
    Inline,
    LoadingPage,
    Link,
    ModalTitle,
    ModalSubtitle,
    ModalContent,
    ModalActions,
    ModalFooter,
    Stack,
    Text,
    tokens,
    Tooltip,
    Box,
} from '@pleo-io/telescope'

import type {PlanResponse} from '~/api/deimos/billing'
import {usePlans} from '~/api/deimos/billing'
import {PlanType} from '~/api/types/plan'
import {dayjs} from '~/dates/dayjs'
import {ContactSupport} from '~/feature-ui/contact-support'
import {Calendly} from '~/generic-ui/calendly'
import {SegmentedControl} from '~/generic-ui/segmented-control/segmented-control'
import {EmployeeStatus} from '~/shell/employee-status'
import {getLocale, useUserMutations, useCompanyUser} from '~/shell/user'

import {FeaturesList} from './features-list'
import {getCalendlyUrlForPlanUpdate} from './get-calendly-url-for-plan-update'
import {
    isTeamPlan,
    availableTeamPlansForUserToChoose,
    availableTeamPlansPreferredOrder,
    isProOrPremium,
    availablePlansForUserToChoose,
    availablePlansPreferredOrder,
    getPlanNameCamelCase,
    getMappedPlan,
    getPlanTypeAmountInfo,
} from './get-plan-details'

const FlexSimple = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: center;
`

const FlexColumnSpaceBetween = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
`

const StyledAmount = styled.div`
    font-size: ${tokens.font5XLarge};
    line-height: ${tokens.font5XLarge};
    color: ${tokens.shade800};
    margin-right: ${tokens.spacing12};
    margin-top: ${tokens.spacing4};

    display: flex;
    flex-direction: column;
    justify-content: center;
`

const CurrentPlanLabel = styled(Text).attrs({
    variant: 'xsmall-subtle',
    as: 'div',
})`
    color: ${tokens.shade800};
    background: ${tokens.pink300};
    padding: ${tokens.spacing2} ${tokens.spacing4};
    border-radius: ${tokens.arc4};
`

export interface ModalManagePlanProps {
    trialEndDate: Date
    isInTrial: boolean
    currentPlan: PlanType
    isSubscriptionEmpty: boolean
    updatePlan: (plan: PlanType, close: () => void, setLoading: (b: boolean) => void) => {}
    onDismiss: () => void
    email: string
    isDismissible: boolean
    isFreemiumConfirm?: boolean
    showModalConfirmCallback?: () => void
}

interface IsAvailablePlanProps {
    currentPlan: PlanType
    plans: PlanResponse[]
}
const filterAndSortAvailablePlans = ({currentPlan, plans}: IsAvailablePlanProps) => {
    if (isTeamPlan.includes(currentPlan) || currentPlan === PlanType.FREE) {
        const teamPlans = plans
            .filter(({type, interval, intervalCount, userCommitment}) => {
                if (availableTeamPlansForUserToChoose.includes(type)) {
                    if (type === PlanType.TEAM) {
                        return (
                            interval &&
                            interval === 'MONTH' &&
                            intervalCount === 1 &&
                            !userCommitment
                        )
                    } else if (type === PlanType.BUSINESS) {
                        return (
                            interval &&
                            interval === 'YEAR' &&
                            intervalCount === 1 &&
                            !userCommitment
                        )
                    } else if (type === PlanType.FREE) {
                        return true
                    }
                }
                return false
            })
            .sort(
                (a, b) =>
                    availableTeamPlansPreferredOrder.indexOf(a.type) -
                    availableTeamPlansPreferredOrder.indexOf(b.type),
            )
        return teamPlans
    } else if (isProOrPremium.includes(currentPlan)) {
        return plans
            .filter(({type}) => availablePlansForUserToChoose.includes(type))
            .sort(
                (a, b) =>
                    availablePlansPreferredOrder.indexOf(a.type) -
                    availablePlansPreferredOrder.indexOf(b.type),
            )
    }

    return undefined
}

interface PlanAmountHeaderProps {
    amountDecimal: number
    currency: string
    isCurrent: boolean
    planInfo: string
    isUpgradeToPremium: boolean
}

const PlanAmountHeader: FC<PlanAmountHeaderProps> = ({
    amountDecimal,
    currency,
    isCurrent,
    planInfo,
    isUpgradeToPremium,
}) => {
    const amountFormatted = new Intl.NumberFormat(getLocale()).format(amountDecimal)
    return (
        <Box paddingTop={10} paddingBottom={36}>
            {!isUpgradeToPremium ? (
                <>
                    <FlexSimple>
                        <StyledAmount>{amountFormatted}</StyledAmount>
                        <FlexColumnSpaceBetween>
                            {isCurrent ? (
                                <CurrentPlanLabel>
                                    <Trans>Current</Trans>
                                </CurrentPlanLabel>
                            ) : (
                                <div />
                            )}
                            <Text color="shade800">{currency}</Text>
                        </FlexColumnSpaceBetween>
                    </FlexSimple>

                    <FlexSimple>
                        <Text color="shade600">{planInfo}</Text>
                    </FlexSimple>
                </>
            ) : (
                <Box>
                    <Text align="center">
                        <Trans>
                            Get more from Pleo - talk to our sales team for pricing information
                        </Trans>
                    </Text>
                </Box>
            )}
        </Box>
    )
}

export const ModalManagePlan: FC<ModalManagePlanProps> = ({
    trialEndDate,
    isInTrial,
    currentPlan,
    updatePlan,
    onDismiss,
    isSubscriptionEmpty,
    email,
    isDismissible,
    isFreemiumConfirm,
    showModalConfirmCallback,
}) => {
    const planNameCamelCaseList = getPlanNameCamelCase()
    const plansMappedToRegularTypes = getMappedPlan()

    const [planSelected, setPlanSelected] = useState<PlanType>(
        plansMappedToRegularTypes[currentPlan],
    )
    const [isLoading, setLoading] = useState(false)
    const [isMeetingVisible, setIsMeetingVisible] = useState(false)

    const {data: plans} = usePlans()
    const {
        company: {numberOfUsers},
    } = useCompanyUser()
    const {setEmployeeStateKey, forceRefetchUser} = useUserMutations()

    const noChangeForPlan = plansMappedToRegularTypes[currentPlan] === planSelected

    const planTypeAmountInfo = getPlanTypeAmountInfo()

    const isUpgrade = isProOrPremium.includes(currentPlan)
        ? availablePlansPreferredOrder.indexOf(planSelected) >
          availablePlansPreferredOrder.indexOf(plansMappedToRegularTypes[currentPlan])
        : availableTeamPlansPreferredOrder.indexOf(planSelected) >
          availableTeamPlansPreferredOrder.indexOf(currentPlan)

    const isUpgradeToPremium =
        isUpgrade && [PlanType.PREMIUM, PlanType.YEARLY, PlanType.BUSINESS].includes(planSelected)

    const getIsNotEligible = () => {
        const planObj = plans?.find(({type}) => type === planSelected)
        const isEligible = Boolean(planObj?.eligibility)

        return !isEligible && planSelected === PlanType.FREE
    }

    const isConfirmPro = isFreemiumConfirm && planSelected === PlanType.PRO

    const getTooltipContent = () => {
        if (
            [PlanType.PREMIUM, PlanType.BUSINESS].includes(
                plansMappedToRegularTypes[currentPlan],
            ) &&
            ![PlanType.PREMIUM, PlanType.BUSINESS].includes(planSelected)
        ) {
            if (currentPlan === PlanType.BUSINESS) {
                return t`To downgrade from Business, please contact your account executive.`
            }
            return t`To downgrade from Premium, please contact your account executive.`
        } else if (getIsNotEligible()) {
            return (
                <Trans>
                    You have more than 5 members added, to downgrade to this plan, you need to have
                    5 members or fewer.
                </Trans>
            )
        }
        return undefined
    }

    const isTooltipVisible = getTooltipContent() !== undefined

    const handleSetMeeting = () => {
        if (isMeetingVisible) {
            onDismiss()
        } else {
            setIsMeetingVisible(true)
        }
    }

    const filteredPlans = plans
        ? filterAndSortAvailablePlans({
              currentPlan: plansMappedToRegularTypes[currentPlan],
              plans,
          })
        : undefined

    if (!filteredPlans || isLoading) {
        return <LoadingPage />
    }

    const handlePeopleEscape = () => {
        if (isFreemiumConfirm) {
            onDismiss()
            setEmployeeStateKey(EmployeeStatus.hasSeenEndTrialModal, true)
        }
    }

    const handleDismiss = async () => {
        onDismiss()
        if (isFreemiumConfirm) {
            await forceRefetchUser()
            await setEmployeeStateKey(EmployeeStatus.hasSeenEndTrialModal, true)
            if (planSelected === PlanType.FREE && showModalConfirmCallback) {
                showModalConfirmCallback()
            }
        }
    }

    const submitButton = (
        <Button
            data-testid="manage-plan-button"
            onClick={(e) => {
                e.persist()
                setLoading(true)
                updatePlan(planSelected, async () => handleDismiss(), setLoading)
            }}
            variant="primary"
            disabled={
                getIsNotEligible() ||
                isSubscriptionEmpty ||
                isLoading ||
                (noChangeForPlan && !isFreemiumConfirm) ||
                [PlanType.PREMIUM, PlanType.BUSINESS].includes(
                    plansMappedToRegularTypes[currentPlan],
                )
            }
        >
            {isConfirmPro ? <Trans>Confirm</Trans> : <Trans>Change plan</Trans>}
        </Button>
    )

    return (
        <>
            <ModalTitle>
                {isFreemiumConfirm ? (
                    <Trans>Your free trial is almost over...</Trans>
                ) : (
                    <Trans>Manage plan</Trans>
                )}
            </ModalTitle>
            {isFreemiumConfirm && (
                <ModalSubtitle>
                    <Trans>
                        Stay on the full version of Pleo by confirming below. Or, if you want to
                        switch to Pleo Free for up to 5 users, select Free
                    </Trans>
                </ModalSubtitle>
            )}
            {isInTrial && !isFreemiumConfirm && (
                <ModalSubtitle>
                    <Trans>
                        Your subscription will start on {dayjs(trialEndDate).format('LL')}
                    </Trans>
                </ModalSubtitle>
            )}
            {isSubscriptionEmpty && !isInTrial && (
                <ModalSubtitle>
                    <Trans>Legacy plan</Trans>
                </ModalSubtitle>
            )}

            <ModalContent align="left" data-testid="billing-modal-manage-plan">
                <Stack space={20} alignContent="center" stretch>
                    <Inline justifyContent="center">
                        <SegmentedControl
                            value={planSelected}
                            onChange={(val) => {
                                setPlanSelected(val)
                                setIsMeetingVisible(false)
                            }}
                            options={filteredPlans.map((item) => ({
                                value: item.type,
                                label: planNameCamelCaseList[item.type],
                            }))}
                            fontSize={tokens.fontSmall}
                        />
                    </Inline>

                    {filteredPlans.map(({type, amountDecimal, currency, country, id}) => {
                        if (type === planSelected) {
                            return (
                                <Stack
                                    justifyContent="center"
                                    stretch
                                    data-testid={`billing-modal-manage-plan-${type}`}
                                    key={id}
                                >
                                    {!isMeetingVisible ? (
                                        <>
                                            <PlanAmountHeader
                                                amountDecimal={amountDecimal || 0}
                                                currency={currency || ''}
                                                isCurrent={currentPlan === type}
                                                planInfo={planTypeAmountInfo[type]}
                                                isUpgradeToPremium={isUpgradeToPremium}
                                            />
                                            <FeaturesList
                                                isCurrent={
                                                    plansMappedToRegularTypes[currentPlan] === type
                                                }
                                                plan={plansMappedToRegularTypes[currentPlan]}
                                                planSelected={type}
                                            />
                                        </>
                                    ) : (
                                        <div data-testid="calendly-iframe">
                                            <Calendly
                                                minWidth="420px"
                                                height="500px"
                                                dataUrl={getCalendlyUrlForPlanUpdate(
                                                    country,
                                                    numberOfUsers || 0,
                                                )}
                                                email={email}
                                                name={planNameCamelCaseList[type]}
                                                hideDetails
                                            />
                                        </div>
                                    )}
                                </Stack>
                            )
                        }
                        return null
                    })}
                </Stack>
            </ModalContent>
            <Box marginTop={16}>
                <ModalActions>
                    <Stack space={20} align="center">
                        <Inline space={16}>
                            {isDismissible && (
                                <Button variant="secondary" onClick={() => onDismiss()}>
                                    <Trans>Cancel</Trans>
                                </Button>
                            )}
                            {!isUpgradeToPremium ? (
                                isTooltipVisible ? (
                                    <Tooltip content={getTooltipContent()}>{submitButton}</Tooltip>
                                ) : (
                                    submitButton
                                )
                            ) : (
                                <Button variant="primary" onClick={() => handleSetMeeting()}>
                                    {!isMeetingVisible ? (
                                        <Trans>Set up meeting</Trans>
                                    ) : (
                                        <Trans>Confirm</Trans>
                                    )}
                                </Button>
                            )}
                        </Inline>
                        {isSubscriptionEmpty && (
                            <ContactSupport
                                chatLabel={t`Contact us to change your plan`}
                                emailLabel={t`Contact us to change your plan`}
                                emailSubject={t`I would like to change my plan`}
                            />
                        )}
                    </Stack>
                </ModalActions>
            </Box>
            {getIsNotEligible() && (
                <ModalFooter align="center">
                    <Trans>
                        You have more than 5 members added. To downgrade to Free, you need to have 5
                        members or fewer. Go to{' '}
                        {
                            <Link
                                as={RouterLink}
                                to="/people"
                                onClick={() => handlePeopleEscape()}
                            >{t`People`}</Link>
                        }{' '}
                        to manage your team.
                    </Trans>
                </ModalFooter>
            )}
        </>
    )
}

ModalManagePlan.defaultProps = {
    currentPlan: PlanType.PRO,
}

export default ModalManagePlan
